import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, gbp, num, dt } from "@/lib/api";
import { PageHeader, Pager, StageBadge } from "@/components/tam/ui";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Filter({ label, value, options, onChange, testId }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="w-full sm:w-44 bg-white" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label}</SelectItem>
        {options.map((o) => {
          const { value: v, label: l } = typeof o === "string" ? { value: o, label: o } : o;
          return <SelectItem key={v} value={v}>{l}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  );
}

export default function Accounts() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ owners: [], reps: [], bands: [], stages: [] });
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [q, setQ] = useState(params.get("q") || "");
  const f = Object.fromEntries(["owner", "rep", "band", "stage", "q", "page"].map((k) => [k, params.get(k) || ""]));

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    api.get("/accounts", { params: { ...f, page: f.page || 1 } }).then((r) => setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => {
    const t = setTimeout(() => q !== f.q && set("q", q), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    if (k !== "page") n.delete("page");
    setParams(n);
  };

  return (
    <div data-testid="accounts-page">
      <PageHeader title="Accounts" subtitle="Lost & non-trading customers – select one to log calls, emails and progress" />
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, account no, contact…" className="pl-9 bg-white" data-testid="accounts-search-input" />
        </div>
        <Filter label="account managers" value={f.owner} options={meta.owners} onChange={(v) => set("owner", v)} testId="filter-owner" />
        <Filter label="reps" value={f.rep} options={meta.reps} onChange={(v) => set("rep", v)} testId="filter-rep" />
        <Filter label="bands" value={f.band} options={meta.bands} onChange={(v) => set("band", v)} testId="filter-band" />
        <Filter label="stages" value={f.stage} options={meta.stages} onChange={(v) => set("stage", v)} testId="filter-stage" />
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[940px]" data-testid="accounts-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr className="text-left">
              <th className="px-4 py-3">Acc No</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Sheet / Rep</th>
              <th className="px-4 py-3">Band</th><th className="px-4 py-3">Last traded</th><th className="px-4 py-3 text-right">Total qty</th><th className="px-4 py-3 text-right">Total value</th>
              <th className="px-4 py-3">Stage</th><th className="px-4 py-3">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`account-row-${a.acc_no}`}>
                <td className="px-4 py-3 font-mono text-xs">{a.acc_no}</td>
                <td className="px-4 py-3"><Link to={`/accounts/${a.id}`} className="font-medium text-[#0B132B] hover:text-[#D9232A]" data-testid={`account-link-${a.acc_no}`}>{a.name}</Link>
                  {a.contact?.contact_name && <p className="text-xs text-slate-500">{a.contact.contact_name}</p>}</td>
                <td className="px-4 py-3 text-xs">{a.owner}<br /><span className="text-slate-500">{a.rep}</span></td>
                <td className="px-4 py-3 text-xs">{a.price_band}</td>
                <td className="px-4 py-3 font-mono text-xs">{a.last_trading_year || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{num(a.total_qty)}</td>
                <td className="px-4 py-3 text-right font-mono">{gbp(a.total_value)}</td>
                <td className="px-4 py-3"><StageBadge stage={a.stage} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{a.last_activity_at ? dt(a.last_activity_at) : "—"}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No accounts match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={(p) => set("page", String(p))} />
    </div>
  );
}
